import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { differenceInDays, addDays, getUtcDate } from "utils/timeUtil";

interface AddChannelInput {
  activatedChannels: number;
  expirationDate: Date;
  addChannels: number;
  duration: number;
}

type Props = {
  registeredChannel: number;
  expiration?: string | null;
};

const normalizeExpirationDate = (
  expiration: string | null | undefined,
  today: Date
): Date => {
  if (!expiration) return today;

  const parsed = getUtcDate(
    expiration.length === 10 ? `${expiration}T00:00:00.000Z` : expiration
  );

  return differenceInDays(parsed, today) < 0 ? today : parsed;
};

export function usePurchaseCalculator(props: Props) {
  const today = useMemo(() => getUtcDate(new Date()), []);

  const [inputs, setInputs] = useState<AddChannelInput>(() => ({
    activatedChannels: props.registeredChannel,
    expirationDate: props.expiration
      ? getUtcDate(props.expiration)
      : getUtcDate(new Date()),
    addChannels: 0,
    duration: 1,
  }));

  const { activatedChannels, expirationDate, addChannels, duration } = inputs;

  const onChangePurchase = ({ name, value }: { name: string; value: any }) => {
    setInputs((inp) => {
      return {
        ...inp,
        [name]: value,
      };
    });
  };

  // Reset addChannels to 0 when expiration date equals today
  useEffect(() => {
    const today = getUtcDate(new Date());
    if (expirationDate && differenceInDays(expirationDate, today) < 0) {
      onChangePurchase({ name: "addChannels", value: 0 });
    }
  }, [expirationDate]);

  const purchaseCalculations = useMemo(() => {
    const daysUntilExpiration = differenceInDays(expirationDate, today);

    const unitDays = daysUntilExpiration * activatedChannels;

    // When 0 new cameras, new expiration equals current expiration
    if (addChannels === 0) {
      return {
        unitDays,
        newLicenseUnitOfDays: 0,
        newNumberOfCameras: activatedChannels,
        totalUnitDays: unitDays,
        newExpirationDate: expirationDate,
        newExpirationDays: daysUntilExpiration,
      };
    }

    const newLicenseUnitOfDays = addChannels * duration * 365;
    const newNumberOfCameras = activatedChannels;
    const totalUnitDays = unitDays + newLicenseUnitOfDays;
    const newExpirationDays = totalUnitDays / newNumberOfCameras;
    const calculatedExpirationDate = moment(today)
      .add(Math.floor(newExpirationDays), "days")
      .toDate();

    // Subscription key expiration date = current expiration + license years
    const subscriptionKeyExpirationDate = addDays(
      expirationDate,
      duration * 365
    );

    // Cap the co-termination date at the subscription key expiration date
    const isCapped = calculatedExpirationDate > subscriptionKeyExpirationDate;
    const newExpirationDate = isCapped
      ? subscriptionKeyExpirationDate
      : calculatedExpirationDate;

    return {
      unitDays,
      newLicenseUnitOfDays,
      newNumberOfCameras,
      totalUnitDays,
      newExpirationDate,
      newExpirationDays,
      subscriptionKeyExpirationDate,
      isCapped,
    };
  }, [inputs]);

  return { inputs, onChangePurchase, purchaseCalculations };
}
